import { Heart, Radio, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "../api/client";
import type { RadioStation } from "../api/types";
import { CommandButton } from "../components/CommandButton";
import { PageShell } from "../components/PageShell";
import {
  DEFAULT_ERROR_RECOVERY_ACTION,
  StatusBlock,
} from "../components/StatusBlock";
import { useAudioCoordinator } from "../stores/audioCoordinator";
import { useNavigationStore } from "../stores/navigationStore";
import styles from "../styles/App.module.css";

const LOCAL_FALLBACK_RADIO_STATIONS: RadioStation[] = [
  {
    id: "radio-local-fallback-huagu",
    title: "本地戏曲电台",
    description: "先播放本机同源戏曲音频，外部免费电台接入前可用。",
    stream_url: "/api/v1/media-episodes/huagu-001/stream",
    fallback_url: "/api/v1/media-episodes/huagu-002/stream",
    last_health_status: "local_fallback",
    favorite: false,
    stream_safety: {
      status: "allowed",
      host: null,
      resolved_ips: [],
      max_redirects: 0,
      connect_timeout_seconds: 2,
      read_timeout_seconds: 5,
      total_timeout_seconds: 8,
      trace: "RADIO-001",
    },
    fallback_safety: {
      status: "allowed",
      host: null,
      resolved_ips: [],
      max_redirects: 0,
      connect_timeout_seconds: 2,
      read_timeout_seconds: 5,
      total_timeout_seconds: 8,
      trace: "RADIO-001",
    },
  },
];

export function RadioPage() {
  const go = useNavigationStore((state) => state.go);
  const home = useNavigationStore((state) => state.home);
  const play = useAudioCoordinator((state) => state.play);
  const [stations, setStations] = useState<RadioStation[]>([]);
  const [stationsLoaded, setStationsLoaded] = useState(false);
  const [usingFallbackStations, setUsingFallbackStations] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .radioStations()
      .then((result) => {
        if (result.items.length) {
          setStations(result.items);
          setUsingFallbackStations(false);
          setStationsLoaded(true);
          return;
        }
        setStations(LOCAL_FALLBACK_RADIO_STATIONS);
        setUsingFallbackStations(true);
        setStationsLoaded(true);
      })
      .catch((loadError) => {
        setError(
          loadError instanceof Error ? loadError.message : "读取电台失败。",
        );
        setStationsLoaded(true);
      });
  }, []);

  return (
    <PageShell title="听电台" where="电台页">
      {error ? (
        <StatusBlock
          tone="error"
          message={error}
          recoveryAction={DEFAULT_ERROR_RECOVERY_ACTION}
        />
      ) : null}
      {!error && !stationsLoaded ? (
        <StatusBlock message="正在读取电台。" />
      ) : null}
      {!error && stationsLoaded && usingFallbackStations ? (
        <StatusBlock message="暂时没有家人添加的电台，先显示本地戏曲电台。" />
      ) : null}
      {!error && stationsLoaded && stations.length === 0 ? (
        <StatusBlock message="暂时没有电台。请家人稍后添加电台。" />
      ) : null}
      <div className={styles.commandList}>
        <CommandButton
          icon={Heart}
          title="我收藏的电台"
          hint="收藏入口在本页第一项"
          onClick={() => go({ name: "favorites" })}
        />
        {stations.map((station) => (
          <CommandButton
            key={station.id}
            icon={Radio}
            title={station.title}
            hint={station.description}
            onClick={() => {
              void play({
                title: station.title,
                streamUrl: station.stream_url,
                fallbackStreamUrl: station.fallback_url,
                contentType: "radio",
                contentId: station.id,
              });
            }}
          />
        ))}
        <CommandButton
          icon={RotateCcw}
          title="播放失败时返回主菜单"
          hint="电台失败后请使用返回主菜单"
          onClick={home}
        />
      </div>
    </PageShell>
  );
}
